import React, { useState } from 'react';
import styles from './ClientForm.module.css';
import type { ClientInfo } from '@/types';

interface ClientFormProps {
  onSubmit: (info: ClientInfo) => void;
  onPrevious: () => void;
  initialData?: ClientInfo;
  isSubmitting?: boolean;
}

interface FormErrors {
  prenom?: string;
  nom?: string;
  email?: string;
  telephone?: string;
}

export const ClientForm: React.FC<ClientFormProps> = ({
  onSubmit,
  onPrevious,
  initialData,
  isSubmitting = false,
}) => {
  const [formData, setFormData] = useState<ClientInfo>(
    initialData || {
      prenom: '',
      nom: '',
      email: '',
      telephone: '',
      entreprise: '',
      acceptMarketing: false,
      acceptTerms: false,
    }
  );

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());

  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'prenom':
      case 'nom':
        return value.trim() === '' ? 'Ce champ est obligatoire' : undefined;

      case 'email':
        if (value.trim() === '') return 'Ce champ est obligatoire';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return !emailRegex.test(value) ? 'Email invalide' : undefined;

      case 'telephone':
        if (value.trim() === '') return 'Ce champ est obligatoire';
        const phoneRegex = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;
        return !phoneRegex.test(value.replace(/\s/g, ''))
          ? 'Numéro de téléphone invalide'
          : undefined;

      default:
        return undefined;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue,
    }));

    // Validate in real time if field has been touched
    if (touched.has(name) && type !== 'checkbox') {
      const error = validateField(name, value);
      setErrors(prev => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;

    setTouched(prev => new Set(prev).add(name));

    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error,
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    newErrors.prenom = validateField('prenom', formData.prenom);
    newErrors.nom = validateField('nom', formData.nom);
    newErrors.email = validateField('email', formData.email);
    newErrors.telephone = validateField('telephone', formData.telephone);

    setErrors(newErrors);

    // Mark all fields as touched
    setTouched(new Set(['prenom', 'nom', 'email', 'telephone']));

    return !Object.values(newErrors).some(error => error !== undefined);
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSubmit(formData);
  };

  return (
    <div className={styles.container}>
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.header}>
          <span className={styles.emoji}>📝</span>
          <h2 className={styles.title}>Vos informations</h2>
          <p className={styles.description}>Pour recevoir vos recommandations personnalisées</p>
        </div>

        <div className={styles.fields}>
          <div className={styles.fieldGroup}>
            <label htmlFor="prenom" className={styles.label}>
              Prénom <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="prenom"
              name="prenom"
              value={formData.prenom}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`${styles.input} ${
                errors.prenom && touched.has('prenom') ? styles.inputError : ''
              }`}
              disabled={isSubmitting}
              autoComplete="given-name"
            />
            {errors.prenom && touched.has('prenom') && (
              <span className={styles.error}>{errors.prenom}</span>
            )}
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="nom" className={styles.label}>
              Nom <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="nom"
              name="nom"
              value={formData.nom}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`${styles.input} ${
                errors.nom && touched.has('nom') ? styles.inputError : ''
              }`}
              disabled={isSubmitting}
              autoComplete="family-name"
            />
            {errors.nom && touched.has('nom') && <span className={styles.error}>{errors.nom}</span>}
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="email" className={styles.label}>
              Email <span className={styles.required}>*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`${styles.input} ${
                errors.email && touched.has('email') ? styles.inputError : ''
              }`}
              disabled={isSubmitting}
              autoComplete="email"
              placeholder="exemple@email.com"
            />
            {errors.email && touched.has('email') && (
              <span className={styles.error}>{errors.email}</span>
            )}
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="telephone" className={styles.label}>
              Téléphone <span className={styles.required}>*</span>
            </label>
            <input
              type="tel"
              id="telephone"
              name="telephone"
              value={formData.telephone}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`${styles.input} ${
                errors.telephone && touched.has('telephone') ? styles.inputError : ''
              }`}
              disabled={isSubmitting}
              autoComplete="tel"
              placeholder="06 XX XX XX XX"
            />
            {errors.telephone && touched.has('telephone') && (
              <span className={styles.error}>{errors.telephone}</span>
            )}
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="entreprise" className={styles.label}>
              Entreprise (optionnel)
            </label>
            <input
              type="text"
              id="entreprise"
              name="entreprise"
              value={formData.entreprise}
              onChange={handleChange}
              className={styles.input}
              disabled={isSubmitting}
              autoComplete="organization"
            />
          </div>
        </div>

        <div className={styles.checkboxes}>
          <label className={styles.checkbox}>
            <input
              type="checkbox"
              name="acceptMarketing"
              checked={formData.acceptMarketing}
              onChange={handleChange}
              disabled={isSubmitting}
            />
            <span>
              En cochant cette case vous consentez à faire partie de notre liste de diffusion et à recevoir votre bilan. Consultez nos{' '}
              <a href="https://pemlab-paris.fr/policies/terms-of-sale" target="_blank" rel="noopener noreferrer">
                CGV
              </a>{' '}
              💜
            </span>
          </label>

        </div>

        <div className={styles.navigation}>
          <button
            type="button"
            onClick={onPrevious}
            disabled={isSubmitting}
            className={`btn btn-secondary ${styles.button}`}
          >
            ← Retour
          </button>

          <div className={styles.submitButtonContainer}>
            <button
              type="submit"
              disabled={isSubmitting || !formData.acceptMarketing}
              className={`btn btn-primary ${styles.button} ${styles.buttonPrimary} ${styles.buttonGreen} ${
                !formData.acceptMarketing ? styles.buttonDisabled : ''
              }`}
            >
              {isSubmitting ? (
                <>
                  <span className={styles.spinner}>⟳</span>
                  Envoi en cours...
                </>
              ) : (
                'Voir mes produits →'
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};
